import React, { ReactElement, useEffect, useState } from 'react';
import proxyAxios from 'api/proxyAxios';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import Link from 'next/link';
import classNames from 'classnames/bind';

import { apiMap } from 'api/apiMap';
import { setLogged, setUser } from 'redux/actionCreators/userActions';
import { withSessionSsr } from 'lib/withSession';

import { reCaptcha } from 'utils/helpers/reCaptcha';
import { GOOGLE_KEY, host } from 'utils/constants';
import { emptyValidator } from 'utils/validator';
import { getCookie, getSearchParameters } from 'utils/helpers/functions';
import { useModal } from 'components/Modal/Modal.hooks';

import RootLayout from 'layouts/RootLayout';
import Input from 'ui/Input';

import styles from 'pages/auth/sign-in/SignIn.module.scss';

const cn = classNames.bind(styles);

interface IParams {
  login: string;
};

interface IFormData {
  login: string;
  password: string;
};

export default function LogIn(): ReactElement {
  const router = useRouter();
  const { openModal } = useModal();
  const [{ }, setCookie, remove] = useCookies(['user', 'flash_message']);
  const [loaderForm, setLoaderForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<IFormData>({ login: '', password: '' });
  const [errors, setErrors] = useState<IFormData>({ login: '', password: '' });
  const [params, setParams] = useState<IParams | null>(null);

  const dispatch = useDispatch();

  const validator = (): boolean => {
    const loginValidator: boolean = emptyValidator({
      formData,
      setErrors,
      key: 'login',
      minChar: 2,
    });
    const passwordValidator: boolean = emptyValidator({
      formData,
      setErrors,
      key: 'password',
      minChar: 4,
    });
    return loginValidator && passwordValidator;
  };

  useEffect(() => {
    reCaptcha();
    dispatch(setLogged(false));
    const params = getSearchParameters();
    if (params) setParams(params as IParams);
    else setParams({ login: 'nofirst' });
  }, []);

  const submitHandler = async (e: any) => {
    e.preventDefault();
    if (!validator()) return;
    setLoaderForm(true);
    let tokenRes: any;

    await grecaptcha.execute(GOOGLE_KEY, { action: 'submit' }).then((token: any) => (tokenRes = token));

    await proxyAxios
      .post(apiMap.authenticate, { ...formData, 'g-recaptcha-response': tokenRes }, { baseURL: '' })
      .then((res: any) => {
        const { data } = res.data;
        if (res.status === 200) {
          const user = {
            company: data.company_id.name,
            logo: data.company_id.logo,
            validity: data.company_id.validity,
            first_name: data.profile_id.first_name,
            type: data.company_id.account_type,
            name: `${data.profile_id.first_name} ${data.profile_id.last_name}`,
            email: data.profile_id.email,
            phone: data.profile_id.phone,
            isAdmin: data.is_admin,
            hash_id: data.hash_id,
            hash: data.hash,
          }

          dispatch(setUser(user));
          setCookie('user', user, { path: '/' });
          dispatch(setLogged(true));
        }
        router.push(data.is_admin || data.hash_id !== null ? '/directory' : '/directory/virtual-office');
      })
      .catch((error) => {
        const status = error.response?.status;

        if (status === 401 || status === 400) {
          const message = `Login or password is incorrect.`;
          setErrors({
            login: message,
            password: message,
          });
        } else if (status === 403) {
          const message = 'Your account is under verification, our team will contact you as soon as possible.';
          openModal('info', {
            heading: '',
            paragraphs: [
              <>{message}</>,
            ],
            buttonText: 'Main Page',
            buttonUrl: '/',
          });
        } else {
          console.log('sign in failed: ', error.message);
        }
      })
      .finally(() => setLoaderForm(false));
  };

  useEffect(() => {
    const flashCookieValue = getCookie('flash_message');
    const showModalFlag = localStorage.getItem('showModal');

    if (showModalFlag === 'true') {
      if (flashCookieValue && typeof flashCookieValue === 'string') {
        localStorage.setItem('showModal', 'true');
        const result = Buffer.from(flashCookieValue, 'base64').toString('utf8');
        const message = JSON.parse(result).message;
        openModal('info', {
          heading: '',
          paragraphs: [
            <>{message}</>,
          ],
          buttonText: 'Main Page',
          buttonUrl: '/',
        });
        remove('flash_message');
      }
      localStorage.setItem('showModal', 'false');
    } else return;
  }, []);

  return (
    <div className={cn('sign-in')}>
      <form
        className={cn('form', { 'is-hidden': !params })}
        onSubmit={submitHandler}
      >

        <h1 className={cn('form__heading')}>Sign In to DF Alliance</h1>

        <Input
          name="login"
          placeholder="Login or Email"
          error={errors.login}
          setFormData={setFormData}
          setErrors={setErrors}
        />

        <Input
          name="password"
          placeholder="Password"
          error={errors.password}
          setFormData={setFormData}
          setErrors={setErrors}
          type="password"
        />

        <button className={cn('form__button-sign-in', { 'is-disabled': loaderForm })}>
          {loaderForm ? <div className={cn('form__preloader')} /> : ''}
          Sign In
        </button>

        <p className={cn('form__links')}>
          {/* (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774) */}
          {/* <span className={cn('form__link')}>
            Don't have an account yet?{' '}
            <Link href="/auth/sign-up">
              Sign Up
            </Link>
          </span> */}
          <span className={cn('form__link')}>
            <Link href="/auth/forgot-password">
              Forgot password?
            </Link>
          </span>
        </p>
      </form>
    </div>
  );
}

export const getServerSideProps = withSessionSsr(async function ({ req, res }) {
  const { user } = req.session;

  if (!user || !user.profile) {
    return {
      props: {
        title: 'Sign In to Your DF Alliance Account | DF Alliance',
        description:
          'Sign in to DF Alliance for easy access to all your global logistics and freight forwarding needs, including membership benefits and industry resources.',
        keywords: 'logistics members directory',
        path: '/auth/sign-in',
      },
    };
  }

  if (user || user.profile) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      title: 'Sign In to Your DF Alliance Account | DF Alliance',
      description:
        'Sign in to DF Alliance for easy access to all your global logistics and freight forwarding needs, including membership benefits and industry resources.',
      keywords: 'logistics members directory',
      path: '/auth/sign-in',
    },
  };
});

LogIn.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
